// components/SubmissionResult.tsx - Post-submission success/failure component

import React from 'react';
import { formatDate } from '../utils/GatePassUtils';
import './SubmissionResult.css';

interface SubmissionResultProps {
  isSuccess: boolean;
  submissionData?: {
    requestId?: string;
    submissionDate?: string;
    estimatedProcessingTime?: string;
    approvalWorkflowStarted?: boolean;
  };
  errorMessage?: string;
  errorDetails?: string;
  onCreateNewRequest: () => void;
  onViewRequests?: () => void;
  onRetrySubmission?: () => void;
}

export const SubmissionResult: React.FC<SubmissionResultProps> = ({
  isSuccess,
  submissionData,
  errorMessage,
  errorDetails,
  onCreateNewRequest,
  onViewRequests,
  onRetrySubmission
}) => {
  if (isSuccess) {
    return (
      <div className="step-content">
        {/* Success Header */}
        <div className="submission-result-header success">
          <div className="result-icon success">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h2 className="result-title">Gate Pass Request Submitted Successfully!</h2>
          <p className="result-subtitle">
            Your request has been submitted and is now in the approval workflow.
          </p>
        </div>

        {/* Success Details */}
        <div className="step2-selection-container">
          <div className="selection-section">
            <div className="selection-header">
              <h3>
                Request Details
                <span className="selection-count-badge success">
                  Submitted
                </span>
              </h3>
            </div>

            <div className="submission-details-grid">
                <div className="detail-item">
                  <div className="detail-label">Request ID:</div>
                  <div className="detail-value">{submissionData?.requestId || 'Generating...'}</div>
                </div>
                
                <div className="detail-item">
                  <div className="detail-label">Submitted On:</div>
                  <div className="detail-value">
                    {(() => {
                      const raw = submissionData?.submissionDate;
                      const parsed = raw ? new Date(raw) : null;
                      return formatDate(
                        parsed && !isNaN(parsed.getTime()) ? raw! : new Date().toISOString()
                      );
                    })()}
                  </div>
                </div>
                
                <div className="detail-item">
                  <div className="detail-label">Processing Time:</div>
                  <div className="detail-value">
                    {submissionData?.estimatedProcessingTime || '2-3 business days'}
                  </div>
                </div>
                
                <div className="detail-item">
                  <div className="detail-label">Status:</div>
                  <div className="detail-value">
                    <span className="status-badge pending">
                      {submissionData?.approvalWorkflowStarted ? 'In Review' : 'Pending Review'}
                    </span>
                  </div>
                </div>
              </div>
          </div>

          {/* Next Steps */}
          <div className="selection-section">
            <div className="selection-header">
              <h3>
                Next Steps
                <span className="selection-count-badge">
                  What happens now?
                </span>
              </h3>
            </div>

            <div className="next-steps-content">
                <div className="step-item">
                  <div className="step-number">1</div>
                  <div className="step-text">
                    <strong>Review Process:</strong> Your request will be reviewed by the selected approvers
                    {submissionData?.approvalWorkflowStarted && ' (review has already begun)'}
                  </div>
                </div>
                
                <div className="step-item">
                  <div className="step-number">2</div>
                  <div className="step-text">
                    <strong>Notification:</strong> You'll receive email updates on the approval status
                  </div>
                </div>
                
                <div className="step-item">
                  <div className="step-number">3</div>
                  <div className="step-text">
                    <strong>Gate Access:</strong> Once approved, you'll receive your gate pass credentials
                  </div>
                </div>
              </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="submission-actions">
          <button
            onClick={onCreateNewRequest}
            className="selection-btn primary"
          >
            Create New Request
          </button>
          
          {onViewRequests && (
            <button
              onClick={onViewRequests}
              className="selection-btn secondary"
            >
              View All Requests
            </button>
          )}
        </div>
      </div>
    );
  }

  // Error/Failure State
  return (
    <div className="step-content">
      {/* Error Header */}
      <div className="submission-result-header error">
        <div className="result-icon error"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></div>
        <h2 className="result-title">Submission Failed</h2>
        <p className="result-subtitle">
          We encountered an issue while submitting your gate pass request.
        </p>
      </div>

      {/* Error Details */}
      <div className="step2-selection-container">
        <div className="selection-section">
          <div className="selection-header">
            <h3>
              Error Information
              <span className="selection-count-badge error">
                Failed
              </span>
            </h3>
          </div>

          <div className="error-details">
              <div className="error-message">
                <strong>Error:</strong> {errorMessage || 'An unexpected error occurred'}
              </div>
              
              {errorDetails && (
                <div className="error-details-text">
                  <strong>Details:</strong>
                  <div className="error-details-content">
                    {errorDetails}
                  </div>
                </div>
              )}
              
              <div className="error-suggestions">
                <strong>What you can do:</strong>
                <ul>
                  <li>Check your internet connection and try again</li>
                  <li>Verify all required fields are completed</li>
                  <li>Ensure your file attachments meet the requirements</li>
                  <li>Contact support if the problem persists</li>
                </ul>
              </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="submission-actions">
        {onRetrySubmission && (
          <button
            onClick={onRetrySubmission}
            className="selection-btn primary"
          >
            Try Again
          </button>
        )}
        
        <button
          onClick={onCreateNewRequest}
          className="selection-btn secondary"
        >
          Start Over
        </button>
        
        {onViewRequests && (
          <button
            onClick={onViewRequests}
            className="selection-btn secondary"
          >
            View Saved Drafts
          </button>
        )}
      </div>
    </div>
  );
};